import { Cancel01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Box, Button, Drawer, IconButton, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import { INITIAL_LOCATION } from "@/components/map/Map";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { useAccessControl } from "@/features/access-control";
import useSnackbar from "@/hooks/useSnackbar";
import { useAddBranchMutation, useUpdateBranchMutation } from "@/features/organizations/api/branchApi";
import { Branch, Schedule } from "@/types/branch";
import { closeBranchDrawer } from "@/store/uiSlice";
import useOrganizations from "@/features/organizations/useOrganizations";
import GeneralView from "./components/General.view";
import ServicesView from "./components/Services.view";
import ScheduleView from "./components/Schedule.view";

const validationSchema = Yup.object().shape({
  title: Yup.string().strict(true).min(4, "Muy Corto!").max(50, "Muy Largo!").required("Campo requerido"),
  whatsapp: Yup.string().min(8, "Muy Corto!").max(8, "Muy Largo!").required("Campo requerido"),
  isMainBranch: Yup.boolean(),
  location: Yup.object().shape({
    address: Yup.string().strict(true).required("Campo requerido"),
    latitude: Yup.number(),
    longitude: Yup.number(),
  }),
  services: Yup.array().of(Yup.string()),
});

const BranchDrawer = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { organizationId } = useParams<{ organizationId: string }>();
  const { currentOrganization } = useOrganizations();
  const [addBranch, { isLoading }] = useAddBranchMutation();
  const [updateBranch, updateMutation] = useUpdateBranchMutation();
  const [openSnackbar] = useSnackbar();
  const { hasAccess } = useAccessControl();
  const isOpen = useAppSelector((state) => state.ui.isBranchDrawerOpen);

  const selectedBranchId = searchParams.get("selectedBranch");
  const [activeTab, setActiveTab] = useState(0);

  const isNewBranch = useMemo(() => !selectedBranchId, [selectedBranchId]);

  const selectedBranch = useMemo(() => {
    return currentOrganization?.branches?.find((branch) => branch.id === selectedBranchId);
  }, [currentOrganization, selectedBranchId]);

  const handleClose = () => {
    dispatch(closeBranchDrawer());
    searchParams.delete("selectedBranch");
    setSearchParams(searchParams);
  };

  const initialValues = useMemo(() => {
    return {
      id: selectedBranch?.id ?? "",
      title: selectedBranch?.title ?? "",
      whatsapp: selectedBranch?.whatsapp ?? "",
      location: {
        address: selectedBranch?.location?.address ?? "",
        latitude: selectedBranch?.location?.latitude ?? INITIAL_LOCATION.lat,
        longitude: selectedBranch?.location?.longitude ?? INITIAL_LOCATION.lng,
      },
      isMainBranch: selectedBranch?.isMainBranch ?? false,
      schedule: (selectedBranch?.schedule ?? {}) as Schedule,
      services: selectedBranch?.services ?? [],
    };
  }, [selectedBranch]);

  const handleSubmit = async (values: Branch) => {
    if (isNewBranch && hasAccess("create.branch")) {
      const response = await addBranch({
        draft: values as Branch,
        organizationId: organizationId as string,
      });

      if (!("error" in response)) {
        openSnackbar("Sucursal creada", "success");
        dispatch(closeBranchDrawer());
      } else {
        openSnackbar("Error al crear la sucursal", "error");
      }
    }
    if (!isNewBranch && hasAccess("update.branch")) {
      const response = await updateBranch({
        branch: values,
        organizationId: organizationId as string,
        branchId: selectedBranch?.id as string,
      });
      if (!("error" in response)) {
        openSnackbar("Sucursal actualizada exitosamente", "success");
        dispatch(closeBranchDrawer());
      } else {
        openSnackbar("Error al actualizar la sucursal", "error");
      }
    }
  };

  return (
    <Stack>
      <Drawer open={isOpen} onClose={handleClose}>
        <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
          {({ values }) => {
            return (
              <Form>
                <>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      padding: 2,
                    }}
                  >
                    <Typography variant="h2">{isNewBranch ? "Nueva sucursal" : `Editar sucursal - ${values.title}`}</Typography>
                    <Paper
                      sx={{
                        borderRadius: "50%",
                      }}
                    >
                      <IconButton onClick={handleClose} color="primary">
                        <HugeiconsIcon icon={Cancel01Icon} />
                      </IconButton>
                    </Paper>
                  </Stack>

                  <Box sx={{ borderBottom: 1, borderColor: "divider" }} px={2}>
                    <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
                      <Tab label="General" />
                      <Tab label="Servicios" />
                      <Tab label="Horarios" />
                    </Tabs>
                  </Box>

                  <Stack spacing={2} flex={1}>
                    {activeTab === 0 && <GeneralView />}
                    {activeTab === 1 && <ServicesView />}
                    {activeTab === 2 && <ScheduleView />}
                  </Stack>

                  <Stack direction={"row"} padding={2} spacing={2} justifyContent={"flex-start"}>
                    {isNewBranch ? (
                      hasAccess("create.content") ? (
                        <Button color="success" type="submit" disabled={isLoading}>
                          Crear
                        </Button>
                      ) : null
                    ) : hasAccess("update.content") ? (
                      <Button color="success" type="submit" disabled={updateMutation.isLoading}>
                        Guardar
                      </Button>
                    ) : null}
                    <Button variant="text" color="primary" onClick={handleClose} disabled={isLoading}>
                      Cancelar
                    </Button>
                  </Stack>
                </>
              </Form>
            );
          }}
        </Formik>
      </Drawer>
    </Stack>
  );
};

export default BranchDrawer;
